import React from 'react';
import './ChatSuggestionCard.css';

export const getDefaultResolvedLabel = (result) => {
  if (result === 'accepted') return '기억했어요';
  if (result === 'dismissed') return '이번만 반영했어요';
  if (result === 'undone') return '저장을 취소했어요';
  // opened(이어서 할 작업 → 이전 대화 이동)는 카드 자체를 숨긴다.
  return '';
};

const MemoryIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    viewBox="0 0 24 24"
    width="18"
  >
    <path
      d="M12 3.5a5.5 5.5 0 0 0-5.45 4.74A4.5 4.5 0 0 0 7.5 17H9v2.5a1 1 0 0 0 1.62.78L14.7 17H16a4.5 4.5 0 0 0 1.45-8.76A5.5 5.5 0 0 0 12 3.5Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
);

const HandoffIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    viewBox="0 0 24 24"
    width="18"
  >
    <rect
      height="16"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.8"
      width="16"
      x="4"
      y="4"
    />
    <path
      d="M8.5 12h7m0 0-2.6-2.6M15.5 12l-2.6 2.6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
);

const FolderIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    viewBox="0 0 24 24"
    width="18"
  >
    <path
      d="M3.75 7.25A1.75 1.75 0 0 1 5.5 5.5h4.1l1.7 1.75H18.5a1.75 1.75 0 0 1 1.75 1.75v9.25A1.75 1.75 0 0 1 18.5 20H5.5A1.75 1.75 0 0 1 3.75 18.25V7.25Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
);

const ResumeIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    viewBox="0 0 24 24"
    width="18"
  >
    <circle
      cx="12"
      cy="12"
      r="8.25"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M12 8v4.2l2.8 1.6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    viewBox="0 0 24 24"
    width="16"
  >
    <path
      d="m5 12.5 4.2 4.2L19 7"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

// 새 탭으로 열리는 버튼 표시. 글자를 더하지 않고 아이콘만 붙인다.
const NewTabIcon = () => (
  <svg
    aria-hidden="true"
    className="chat-suggestion-card__new-tab"
    fill="none"
    height="14"
    viewBox="0 0 24 24"
    width="14"
  >
    <path
      d="M14 4h6v6M20 4l-9 9M19 14v4.5A1.5 1.5 0 0 1 17.5 20h-11A1.5 1.5 0 0 1 5 18.5v-11A1.5 1.5 0 0 1 6.5 6H11"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const SuggestionIcon = ({ type }) => {
  if (type === 'category_handoff' || type === 'batch_instead_of_single') {
    return <HandoffIcon />;
  }
  if (
    type === 'file_to_library'
    || type === 'save_output_as_example'
    || type === 'reuse_eval_plan'
    || type === 'ai_reference_consent'
  ) {
    return <FolderIcon />;
  }
  if (type === 'premium_gap_style') {
    return <ResumeIcon />;
  }
  if (type === 'next_tool_nudge') {
    return <HandoffIcon />;
  }
  return <MemoryIcon />;
};

const ChatSuggestionCard = ({
  suggestion,
  result = null,
  busy = false,
  onAction,
  resolvedLabel,
  resolvedLinkLabel = '기억 보기',
  onResolvedLink,
  onUndo,
  beforeActions = null,
  busyLabel = '저장 중...',
}) => {
  if (!suggestion) return null;
  // 이전 대화로 이동한 뒤에는 카드가 필요 없다. 돌아와도 다시 띄우지 않는다.
  if (result === 'opened') return null;

  const isResolved = result === 'accepted'
    || result === 'dismissed'
    || result === 'undone';

  const handleAction = (action) => {
    if (busy || !action || typeof onAction !== 'function') return;
    onAction(action, suggestion);
  };

  if (isResolved) {
    const label = resolvedLabel || getDefaultResolvedLabel(result);
    const showUndo = result === 'accepted' && typeof onUndo === 'function';
    const showLink = typeof onResolvedLink === 'function';

    return (
      <section
        aria-label={label}
        className="chat-suggestion-card chat-suggestion-card--resolved"
      >
        <div className="chat-suggestion-card__resolved-line">
          <span aria-hidden="true" className="chat-suggestion-card__check">
            <CheckIcon />
          </span>
          <span className="chat-suggestion-card__resolved-label">{label}</span>
          {showLink && (
            <button
              aria-label={resolvedLinkLabel}
              className="chat-suggestion-card__text-btn"
              onClick={onResolvedLink}
              type="button"
            >
              {resolvedLinkLabel}
            </button>
          )}
          {showUndo && (
            <button
              aria-label="실행취소"
              className="chat-suggestion-card__text-btn"
              onClick={onUndo}
              type="button"
            >
              실행취소
            </button>
          )}
        </div>
      </section>
    );
  }

  const primary = suggestion.primary || null;
  const secondary = suggestion.secondary || null;
  const primaryLabel = busy ? busyLabel : (primary?.label || '');
  // 기본 버튼이 현재 대화를 떠나 새 탭을 여는 경우(이어하기·이어가기). 접근 가능한 이름은 그대로 두고
  // 아이콘 + title 로만 알린다 — 카드 글자 수를 늘리지 않는다.
  const opensInNewTab = Boolean(suggestion.opensInNewTab) && !busy;

  return (
    <section
      aria-busy={busy || undefined}
      aria-label={suggestion.title || '제안'}
      className="chat-suggestion-card"
    >
      <div className="chat-suggestion-card__header">
        <div aria-hidden="true" className="chat-suggestion-card__icon">
          <SuggestionIcon type={suggestion.type} />
        </div>
        <div className="chat-suggestion-card__copy">
          <strong className="chat-suggestion-card__title">
            {suggestion.title}
          </strong>
          {suggestion.description && (
            <p className="chat-suggestion-card__description">
              {suggestion.description}
            </p>
          )}
        </div>
      </div>

      {suggestion.preview && (
        <p className="chat-suggestion-card__preview">
          {suggestion.preview}
        </p>
      )}

      {beforeActions}

      <div className="chat-suggestion-card__actions">
        {primary && (
          <button
            aria-label={primaryLabel}
            className="chat-suggestion-card__btn chat-suggestion-card__btn--primary"
            disabled={busy}
            onClick={() => handleAction(primary.action)}
            title={opensInNewTab ? '새 탭에서 열려요' : undefined}
            type="button"
          >
            {busy && (
              <span
                aria-hidden="true"
                className="chat-suggestion-card__spinner"
              />
            )}
            <span>{primaryLabel}</span>
            {opensInNewTab && <NewTabIcon />}
          </button>
        )}
        {secondary && (
          <button
            aria-label={secondary.label}
            className="chat-suggestion-card__btn chat-suggestion-card__btn--secondary"
            disabled={busy}
            onClick={() => handleAction(secondary.action)}
            type="button"
          >
            {secondary.label}
          </button>
        )}
      </div>
    </section>
  );
};

export default ChatSuggestionCard;
